import { gql, useMutation } from '@apollo/client';
import {
	Button,
	Card,
	CardActions,
	CardContent,
	Container,
	Grid,
	TextField as MTTextField,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import { Alert } from 'components/ui/alert';
import { PhoneNumberField, TextField } from 'components/ui/fields';
import { AddressField, AddressFieldSkeleton } from 'components/ui/fields/address';
import { PageContent, PageTitle } from 'components/ui/page';
import { useToast } from 'components/ui/toast';
import { Formik, FormikProps } from 'formik';
import { Mutation, MutationOrganizationCreateArgs, OrganizationCreation } from 'middleware-types';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { handleNoResponse, responseHasErrors } from 'utils/errors';
import { refreshToken } from 'utils/session';
import { ALL_USER_ORG_PERMISSIONS } from 'utils/useAssociateUser';
import { useDefaults } from 'utils/useDefaults';
import { geocode } from 'utils/useGeocode';
import { useValidation } from 'utils/useValidation';

/**
 * useOrgForm() - a custom hook to handle the data logic for organization onboarding.
 *
 * @returns
 */
export const useOrgForm = () => {
	const toast = useToast();
	const validation = useValidation('OrganizationCreation');
	const [updateOrg, updateOrgStatus] = useMutation<
		Pick<Mutation, 'organizationCreate'>,
		MutationOrganizationCreateArgs
	>(
		gql`
			mutation organizationCreate($organizationCreation: OrganizationCreation!) {
				organizationCreate(organizationCreation: $organizationCreation) {
					mainPhoneNumber {
						countryCode
						number
					}
				}
			}
		`,
		{
			errorPolicy: 'all',
			refetchQueries: [ALL_USER_ORG_PERMISSIONS],
		}
	);
	const defaults = useDefaults();
	const navigate = useNavigate();

	const initialValues: OrganizationCreation = {
		legalName: '',
		displayName: '',
		systemEmailAddress: '',
		mainPhoneNumber: {
			countryCode: defaults.country.phonePrefix,
			number: '',
		},
		mailingAddress: {
			countryId: defaults.country.id,
			address1: '',
			address2: '',
			municipality: '',
			adminArea1Id: '',
			adminArea2Id: '',
			postalCode: '',
			coordinate: undefined,
		},
	};

	const onSubmit = async (values: OrganizationCreation, redirect: boolean): Promise<boolean> => {
		if (!values.mailingAddress) return Promise.resolve(false);
		return await geocode(values.mailingAddress).then((location) =>
			updateOrg({
				variables: {
					organizationCreation: {
						...values,
						mailingAddress: {
							...values.mailingAddress,
							coordinate: location,
						},
					},
				},
				update: async (cache) => {
					await refreshToken();
					cache.evict({
						id: 'ROOT_QUERY',
						fieldName: `affiliatedOrganizations`,
					});
					cache.evict({
						id: 'ROOT_QUERY',
						fieldName: `allUserOrgPermissions`,
					});
				},
			})
				.then(async (res) => {
					if (responseHasErrors(res.errors, { toast })) {
						return false;
					}
					toast.push('Thank you for adding an Organization to Evolve.', {
						variant: 'success',
					});
					// TODO: Redirect to the Organization dashboard.
					redirect && navigate('/');
					return true;
				})
				.catch(() => {
					handleNoResponse({ toast });
					return false;
				})
		);
	};

	return {
		loading: validation.loading,
		validationSchema: validation.schema,
		initialValues,
		onSubmit,
		error: updateOrgStatus.error,
	};
};

/**
 * The OrgScreen is a create organization page.
 */
export const OrgCreatePage: React.FC = () => {
	const { initialValues, loading, validationSchema, onSubmit, error } = useOrgForm();
	return (
		<PageContent>
			<PageTitle title="Create Organization" />
			<Container>
				<Grid container justifyContent="center">
					<Grid item xs={12} sm={10} md={8}>
						<Formik<OrganizationCreation>
							initialValues={initialValues}
							onSubmit={(vals) => onSubmit(vals, true)}
							validationSchema={validationSchema}
							enableReinitialize>
							{(props: FormikProps<OrganizationCreation>) => (
								<Stack
									alignItems="center"
									justifyContent="center"
									mt={2}
									spacing={2}>
									<img width={75} src="/img/logo-colored.svg" alt="FTEvolve" />
									<Typography variant="h1">Create an Organization</Typography>
									<Typography variant="body2" width="55%" textAlign="center">
										Register to connect with insurance professionals and find
										work in your area.
									</Typography>
									<Card>
										<CardContent>
											<Stack gap={2}>
												{loading ? (
													<OrgFormSkeleton />
												) : (
													<>
														<Typography variant="h3">
															Organization Details
														</Typography>
														<Grid
															container
															spacing={1}
															columnSpacing={2}>
															<Grid xs={12} md={6} item>
																<TextField
																	label="Legal Name"
																	required
																	name="legalName"
																	disabled={props.isSubmitting}
																/>
															</Grid>
															<Grid xs={12} md={6} item>
																<TextField
																	label="Display Name"
																	required
																	name="displayName"
																	disabled={props.isSubmitting}
																/>
															</Grid>
														</Grid>
														<Typography variant="h3">
															Primary Address
														</Typography>
														<AddressField
															name="mailingAddress"
															required
															disabled={props.isSubmitting}
														/>
														<Typography variant="h3">
															Contact Information
														</Typography>
														<Grid
															container
															spacing={1}
															columnSpacing={2}>
															<Grid xs={12} md={4} item>
																<TextField
																	label="Email Address"
																	required
																	name="systemEmailAddress"
																	type="email"
																	disabled={props.isSubmitting}
																/>
															</Grid>
															<Grid xs={12} md={4} item>
																<PhoneNumberField
																	label="Phone Number"
																	required
																	name="mainPhoneNumber"
																	disabled={props.isSubmitting}
																/>
															</Grid>
														</Grid>
													</>
												)}
											</Stack>
											{error && <Alert error={error} />}
											<CardActions>
												<Grid
													container
													justifyContent="flex-end"
													alignItems="stretch">
													<Grid item>
														<Button
															color="primary"
															variant="contained"
															disabled={
																!props.isValid ||
																!props.dirty ||
																props.isSubmitting
															}
															onClick={props.submitForm}>
															{`Create Organization`}
														</Button>
													</Grid>
												</Grid>
											</CardActions>
										</CardContent>
									</Card>
								</Stack>
							)}
						</Formik>
					</Grid>
				</Grid>
			</Container>
		</PageContent>
	);
};

/**
 * OrgFormSkeleton - The skeleton loading for organization forms.
 *
 */
const OrgFormSkeleton = () => (
	<>
		<Typography variant="h3">Organization Details</Typography>
		<Grid container spacing={1} columnSpacing={2}>
			<Grid xs={12} md={6} item>
				<Skeleton width="100%">
					<MTTextField />
				</Skeleton>
			</Grid>
			<Grid xs={12} md={6} item>
				<Skeleton width="100%">
					<MTTextField />
				</Skeleton>
			</Grid>
		</Grid>
		<Typography variant="h3">Organization Primary Address</Typography>
		<AddressFieldSkeleton />
		<Typography variant="h3">Organization Contact Information</Typography>
		<Grid container spacing={1} columnSpacing={2}>
			<Grid xs={12} md={4} item>
				<Skeleton width="100%">
					<MTTextField />
				</Skeleton>
			</Grid>
			<Grid xs={12} md={4} item>
				<Skeleton width="100%">
					<MTTextField />
				</Skeleton>
			</Grid>
		</Grid>
	</>
);
